import { UserProfile, TierInfo, CustomAttributes, Offer, UserSearchParams, PointTransaction, OfferIssuance } from '../types/api'

// API Configuration from environment variables
// SECURITY: no credentials in the client bundle. SessionM/Zendesk/MT auth is injected
// server-side by the Express proxy (server/*-api.js). Only the non-secret retailer id
// remains.
const API_CONFIG = {
  username: '',
  password: '',
  pointsUsername: '',
  pointsPassword: '',
  retailerId: process.env.REACT_APP_SESSIONM_RETAILER_ID || '',
  marianaTek: {
    bearerToken: ''
  },
  zendesk: {
    auth: ''
  }
}

// NOTE: Do not read process.env with a dynamic key (process.env[var]) or enumerate
// process.env in client code - CRA inlines the ENTIRE env object into the bundle,
// leaking every REACT_APP_* value. Secrets are validated server-side instead.

const POINT_SOURCES = {
  'Refer a Friend': {
    id: '9C925ECB-D807-46FF-ABE1-2F6C889120BC',
    account_id: '8CCA4D96-F9EB-45EA-A08B-D35BB7771FCF'
  },
  'Retail': {
    id: '16F94BDD-9F52-42B6-8DC1-634BC0A8C3DA',
    account_id: '79FE713A-7DE8-4243-8B0A-8CC143EB6935'
  },
  'Fuel': {
    id: '0F7C8352-CEE9-4985-B66A-A7B7EF84A150',
    account_id: '79FE713A-7DE8-4243-8B0A-8CC143EB6935'
  },
  'Take a Class': {
    id: 'EDB554C0-DF79-436D-99A4-207F7A60989C',
    account_id: '03FE398B-CFB8-4BE1-B8CB-F5325599A766'
  },
  'Customer Service': {
    id: '6B50D87F-B68C-4A2B-8EA7-ACEA317BF755',
    account_id: '03FE398B-CFB8-4BE1-B8CB-F5325599A766'
  },
  'Completed a Challenge': {
    id: '8a6ec7a5-8022-4ee5-9255-8028e729c0d9',
    account_id: '03FE398B-CFB8-4BE1-B8CB-F5325599A766'
  }
}

export const MAX_STARS_DEPOSIT = 1000000

const AVAILABLE_OFFERS = {
  'Cancellation Forgiveness': '6642aade-0ec4-4392-b614-5945e1deff76',
  'Complimentary Guest Pass': 'b63392ee-35a0-458f-9a03-6bf8b130736d',
  'Complimentary Shipping 1x Use': 'a9836764-b389-4205-94e3-ff03d35f23d9',
  '25% Off Barry\'s Shop': '09edf5ab-da81-455c-9181-318174a0cbfe',
  'FWB- Referral Credit': 'bb653d1d-0473-43ed-a1ae-7e732db7f894',
  'FWB- Referee Credit': 'e884683d-9c9c-4314-bb68-0db667e048f1',
  'Birthday Credit': 'bf1aa60d-311c-4454-a821-e5eca699cc4d'
}

const AVAILABLE_OFFER_ROOT_IDS = {
  'Cancellation Forgiveness': 'c9bd4f0d-5ae5-4655-9a47-68c773c166ad',
  'Complimentary Guest Pass': 'e8174565-373c-4eeb-b5b2-11339d9fbbf0',
  'Complimentary Shipping 1x Use': 'f69d68e9-74ce-4a82-954d-2d9c2d898be3',
  '25% Off Barry\'s Shop': 'b8e68f05-6d4d-4ed3-bf56-0349735795eb',
  'FWB- Referral Credit': 'db376a5a-df0d-4607-8f2f-68702e288209',
  'FWB- Referee Credit': '96408967-f5f0-430f-bc5d-a37a63cb2b2c',
  'Birthday Credit': '3a2bad50-a6eb-42f7-a564-9ce084ab0342'
}

const getAuthHeaders = (usePointsAuth = false) => {
  const credentials = usePointsAuth 
    ? `${API_CONFIG.pointsUsername}:${API_CONFIG.pointsPassword}`
    : `${API_CONFIG.username}:${API_CONFIG.password}`
  
  return {
    'Authorization': `Basic ${btoa(credentials)}`,
    'Content-Type': 'application/json'
  }
}

export const searchUserByEmail = async (email: string): Promise<any> => {
  const response = await fetch(
    `/api/sessionm/users/search?email=${encodeURIComponent(email)}`,
    {
      method: 'GET'
    }
  )
  
  if (!response.ok) {
    return throwServerError(response, 'Failed to search user by email')
  }
  
  return response.json()
}

export const getUserBySessionMId = async (sessionmId: string): Promise<any> => {
  const response = await fetch(
    `/api/sessionm/users/${sessionmId}`,
    {
      method: 'GET'
    }
  )
  
  if (!response.ok) {
    throw new Error('Failed to get user by SessionM ID')
  }
  
  return response.json()
}

export const getUserByExternalId = async (externalId: string): Promise<any> => {
  const response = await fetch(
    `/api/sessionm/external/users/${externalId}`,
    {
      method: 'GET'
    }
  )
  
  if (!response.ok) {
    throw new Error('Failed to get user by External ID')
  }
  
  return response.json()
}

// Surface the server's error body instead of a fixed string. Durable mutation
// responses carry the operation ID and reconciliation guidance the operator
// must see; swallowing them strands in_doubt operations with no handle.
async function throwServerError(response: Response, fallback: string): Promise<never> {
  const errorData: { error?: string; message?: string } = await response.json().catch(() => ({}))
  throw new Error(errorData.error || errorData.message || `${fallback}: ${response.status}`)
}

export const depositPoints = async (userId: string, pointSource: string, amount: number, transactionId?: string): Promise<any> => {
  const sourceConfig = POINT_SOURCES[pointSource as keyof typeof POINT_SOURCES]
  if (!sourceConfig) {
    throw new Error('Invalid point source')
  }

  const baseDepositDetail: any = {
    point_source_id: sourceConfig.id,
    amount: amount,
    reference_id: `${Date.now()}_deposit`,
    reference_type: "deposit_reference",
    rank: 0
  }

  const additionalDepositDetail: any = {
    point_source_id: sourceConfig.id,
    amount: amount,
    point_account_id: "1AB3FFFD-539A-4F37-9002-89D19D95535D",
    reference_id: `${Date.now()}_deposit_additional`,
    reference_type: "deposit_reference",
    rank: 1
  }

  // Add transaction_id if provided
  if (transactionId) {
    baseDepositDetail.transaction_id = transactionId
    additionalDepositDetail.transaction_id = transactionId
  }

  const payload = {
    retailer_id: API_CONFIG.retailerId,
    user_id: userId,
    deposit_details: [
      {
        ...baseDepositDetail,
        point_account_id: sourceConfig.account_id
      },
      additionalDepositDetail
    ],
    allow_partial_success: false,
    disable_event_publishing: false,
    culture: "en-US"
  }

  const response = await fetch(
    '/api/sessionm/points/deposit',
    {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json'
      },
      body: JSON.stringify(payload)
    }
  )
  
  if (!response.ok) {
    await throwServerError(response, 'Failed to deposit points')
  }

  return response.json()
}

export const depositStars = async (userId: string, pointSource: string, amount: number): Promise<any> => {
  if (!POINT_SOURCES[pointSource as keyof typeof POINT_SOURCES]) {
    throw new Error('Invalid point source')
  }

  if (!Number.isSafeInteger(amount) || amount < 1 || amount > MAX_STARS_DEPOSIT) {
    throw new Error(`Amount must be an integer between 1 and ${MAX_STARS_DEPOSIT}`)
  }

  const response = await fetch(
    `/api/sessionm/users/${encodeURIComponent(userId)}/stars/deposit`,
    {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json'
      },
      body: JSON.stringify({
        point_source: pointSource,
        amount
      })
    }
  )

  if (!response.ok) {
    await throwServerError(response, 'Failed to add Stars')
  }

  return response.json()
}

export const spendPoints = async (userId: string, pointSource: string, amount: number): Promise<any> => {
  const sourceConfig = POINT_SOURCES[pointSource as keyof typeof POINT_SOURCES]
  if (!sourceConfig) {
    throw new Error('Invalid point source')
  }

  const payload = {
    retailer_id: API_CONFIG.retailerId,
    user_id: userId,
    spend_details: [
      {
        amount: amount,
        point_account_ids: [sourceConfig.account_id],
        reference_id: `${Date.now()}_spend`,
        reference_type: "spend_reference",
        force_spend: false,
        rank: 0,
        is_return: false
      }
    ],
    allow_partial_success: false,
    culture: "en-US"
  }

  const response = await fetch(
    '/api/sessionm/points/spend',
    {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json'
      },
      body: JSON.stringify(payload)
    }
  )
  
  if (!response.ok) {
    await throwServerError(response, 'Failed to spend points')
  }

  return response.json()
}

export const subtractPoints = async (userId: string, pointSource: string, amount: number): Promise<any> => {
  if (!POINT_SOURCES[pointSource as keyof typeof POINT_SOURCES]) {
    throw new Error('Invalid point source')
  }
  if (!Number.isSafeInteger(amount) || amount < 1 || amount > MAX_STARS_DEPOSIT) {
    throw new Error(`Amount must be an integer between 1 and ${MAX_STARS_DEPOSIT}`)
  }
  const response = await fetch(
    `/api/sessionm/users/${encodeURIComponent(userId)}/stars/subtract`,
    {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ point_source: pointSource, amount })
    }
  )
  if (!response.ok) {
    await throwServerError(response, 'Failed to subtract Stars')
  }
  return response.json()
}

export const getUserOffers = async (userId: string): Promise<any[]> => {
  const response = await fetch(
    `/api/sessionm/users/${encodeURIComponent(userId)}/offers`,
    { method: 'GET' }
  )

  if (!response.ok) {
    throw new Error('Failed to get user offers')
  }
  
  const data = await response.json()
  
  // Extract offers array from response and normalize format
  const offers = data?.payload?.user_offers || data?.user_offers || []
  
  // Ensure we always return an array and map to consistent format
  if (!Array.isArray(offers)) {
    return []
  }
  
  return offers.map((offer: any) => ({
    offer_instance_id: offer.offer_instance_id || offer.id,
    offer_id: offer.offer_id,
    root_offer_id: offer.root_offer_id,
    name: offer.name || '',
    acquire_date: offer.acquire_date || offer.start_date || null,
    expiration_date: offer.expiration_date || offer.end_date || null
  }))
}

export const getUserUsedOffers = async (userId: string): Promise<any[]> => {
  const response = await fetch(
    `/api/sessionm/users/${encodeURIComponent(userId)}/offers?statuses=used`,
    { method: 'GET' }
  )

  if (!response.ok) {
    throw new Error('Failed to get used offers')
  }

  const data = await response.json()
  const offers = data?.payload?.user_offers || data?.user_offers || []

  if (!Array.isArray(offers)) {
    return []
  }

  const now = Date.now()
  return offers
    .map((offer: any) => {
      const status = (offer.status || '').toLowerCase()
      const expDate = offer.expiration_date || offer.end_date || null
      const redeemedDate = offer.redemption_date || offer.redeemed_date || null
      const isRedeemed = status === 'redeemed' || !!redeemedDate
      const isExpired = !isRedeemed && expDate && new Date(expDate).getTime() < now
      return {
        offer_instance_id: offer.offer_instance_id || offer.id,
        offer_id: offer.offer_id,
        name: offer.name || '',
        status: isRedeemed ? 'redeemed' : isExpired ? 'expired' : status,
        acquire_date: offer.acquire_date || offer.start_date || null,
        expiration_date: expDate,
        redemption_date: redeemedDate,
        _isUsed: isRedeemed || isExpired,
      }
    })
    .filter((o: any) => o._isUsed)
}

export const issueOffer = async (userId: string, offerName: string): Promise<any> => {
  const offerId = AVAILABLE_OFFERS[offerName as keyof typeof AVAILABLE_OFFERS]
  if (!offerId) {
    throw new Error('Invalid offer name')
  }

  const payload = {
    user_id: userId,
    offer_id: offerId,
    quantity: 1,
    retailer_id: API_CONFIG.retailerId,
    issue_exact_version: false,
    rollback_option: 0,
    culture: "en-US"
  }

  const response = await fetch(
    '/api/sessionm/offers/issue',
    {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json'
      },
      body: JSON.stringify(payload)
    }
  )
  
  if (!response.ok) {
    await throwServerError(response, 'Failed to issue offer')
  }

  return response.json()
}

// Enhanced offer issuance that also creates Mariana Tek credits
const issueOfferAtPath = async (
  path: string,
  email: string,
  offerName: string,
  failureMessage: string,
): Promise<any> => {
  if (!AVAILABLE_OFFERS[offerName as keyof typeof AVAILABLE_OFFERS]) {
    throw new Error('Invalid offer name')
  }

  const payload = {
    email,
    offerName,
    operationId: crypto.randomUUID()
  }

  const response = await fetch(
    path,
    {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json'
      },
      body: JSON.stringify(payload)
    }
  )
  
  if (!response.ok) {
    await throwServerError(response, failureMessage)
  }

  return response.json()
}

export const issueOfferWithCredit = async (email: string, offerName: string): Promise<any> =>
  issueOfferAtPath(
    '/api/mariana-tek/issue-offer-with-credit',
    email,
    offerName,
    'Failed to issue offer with credit',
  )

export const issuePerkWithCredit = async (email: string, offerName: string): Promise<any> =>
  issueOfferAtPath(
    '/api/mariana-tek/issue-perk-with-credit',
    email,
    offerName,
    'Failed to issue Perk',
  )

// Sync an offer to Mariana Tek only (create MT credit to match existing SessionM offer)
export const syncCreditToMT = async (email: string, offerName: string, expirationDate?: string): Promise<any> => {
  const response = await fetch('/api/mariana-tek/sync-credit-to-mt', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ email, offerName, expirationDate: expirationDate || null, operationId: crypto.randomUUID() })
  })

  if (!response.ok) {
    const errorText = await response.text()
    throw new Error(`Failed to sync credit: ${response.status} - ${errorText}`)
  }

  return response.json()
}

// Remove/revoke an offer: redeems SessionM offer and removes Mariana Tek credit
export const removeOfferWithCredit = async (email: string, offerName: string, offerInstanceId: string): Promise<any> => {
  const payload = {
    email,
    offerName,
    offerInstanceId,
    operationId: crypto.randomUUID()
  }

  const response = await fetch(
    '/api/mariana-tek/remove-offer-with-credit',
    {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json'
      },
      body: JSON.stringify(payload)
    }
  )

  if (!response.ok) {
    const errorText = await response.text()
    throw new Error(`Failed to remove offer: ${response.status} - ${errorText}`)
  }

  return response.json()
}

export const getUserTimeline = async (externalId: string, forceRefresh = false): Promise<any> => {
  const url = forceRefresh 
    ? `/api/sessionm/external/users/${externalId}/timeline?_t=${Date.now()}`
    : `/api/sessionm/external/users/${externalId}/timeline`
    
  const response = await fetch(url, {
    method: 'GET',
    headers: {
      ...(forceRefresh && {
        'Cache-Control': 'no-cache, no-store, must-revalidate',
        'Pragma': 'no-cache',
        'Expires': '0'
      })
    }
  })

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}))
    throw new Error(errorData.message || `Failed to fetch timeline: ${response.status}`)
  }

  return response.json()
}

export const getUserDeposits = async (externalId: string): Promise<any> => {
  const response = await fetch(`/api/sessionm/external/users/${externalId}/deposits`, {
    method: 'GET'
  })

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}))
    throw new Error(errorData.message || `Failed to fetch deposits: ${response.status}`)
  }

  return response.json()
}

export const getPointAccountBalance = async (userId: string): Promise<any> => {
  const payload = {
    retailer_id: API_CONFIG.retailerId,
    user_id: userId,
    point_account_ids: ["B82B0A8A-BDCA-46B0-8895-96097B2AD0A5"],
    culture: "en-US"
  }

  const response = await fetch(
    '/api/sessionm/points/balance',
    {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json'
      },
      body: JSON.stringify(payload)
    }
  )
  
  if (!response.ok) {
    throw new Error('Failed to get point account balance')
  }
  
  return response.json()
}

export const depositPurchasePoints = async (userId: string, amount: number, transactionId: string): Promise<any> => {
  // First deposit to main account
  const payload1 = {
    retailer_id: API_CONFIG.retailerId,
    user_id: userId,
    deposit_details: [
      {
        point_source_id: "16F94BDD-9F52-42B6-8DC1-634BC0A8C3DA",
        amount: amount,
        point_account_id: "79FE713A-7DE8-4243-8B0A-8CC143EB6935",
        reference_id: `${Date.now()}_deposit_additional`,
        reference_type: "deposit_reference",
        transaction_id: transactionId,
        rank: 0
      }
    ],
    allow_partial_success: false,
    disable_event_publishing: false,
    culture: "en-US"
  }

  const response1 = await fetch(
    '/api/sessionm/points/deposit',
    {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json'
      },
      body: JSON.stringify(payload1)
    }
  )

  if (!response1.ok) {
    throw new Error('Failed to deposit purchase points to main account')
  }

  // Second deposit to additional account
  const payload2 = {
    retailer_id: API_CONFIG.retailerId,
    user_id: userId,
    deposit_details: [
      {
        point_source_id: "16F94BDD-9F52-42B6-8DC1-634BC0A8C3DA",
        amount: amount,
        point_account_id: "1AB3FFFD-539A-4F37-9002-89D19D95535D",
        reference_id: `${Date.now()}_deposit_secondary`,
        reference_type: "deposit_reference",
        transaction_id: transactionId,
        rank: 0
      }
    ],
    allow_partial_success: false,
    disable_event_publishing: false,
    culture: "en-US"
  }

  const response2 = await fetch(
    '/api/sessionm/points/deposit',
    {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json'
      },
      body: JSON.stringify(payload2)
    }
  )

  if (!response2.ok) {
    throw new Error('Failed to deposit purchase points to secondary account')
  }

  return await response1.json()
}

export const depositOptInPoint = async (userId: string): Promise<any> => {
  const payload = {
    retailer_id: API_CONFIG.retailerId,
    user_id: userId,
    deposit_details: [
      {
        point_source_id: "1E3E836F-C21A-4345-BB24-BA6141DC5DF1", // Tier Qualifying point source
        amount: 1,
        point_account_id: "B82B0A8A-BDCA-46B0-8895-96097B2AD0A5", // Opt-in point account
        reference_id: `${Date.now()}_optin_deposit`,
        reference_type: "loyalty_optin",
        rank: 0
      }
    ],
    allow_partial_success: false,
    disable_event_publishing: false,
    culture: "en-US"
  }

  const response = await fetch(
    '/api/sessionm/points/deposit',
    {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json'
      },
      body: JSON.stringify(payload)
    }
  )
  
  if (!response.ok) {
    throw new Error('Failed to deposit opt-in point')
  }
  
  return response.json()
}

export const updateUserLoyaltyStatus = async (userId: string): Promise<any> => {
  const payload = {
    user: {
      opted_in: true,
      user_profile: {
        loyalty_status: true
      }
    }
  }

  const response = await fetch(
    `/api/sessionm/users/${userId}`,
    {
      method: 'PUT',
      headers: {
        'Content-Type': 'application/json'
      },
      body: JSON.stringify(payload)
    }
  )
  
  if (!response.ok) {
    throw new Error('Failed to update user loyalty status')
  }
  
  return response.json()
}

export const runTierQualifying = async (userId: string): Promise<any> => {
  const payload = {
    retailer_id: API_CONFIG.retailerId,
    user_id: userId,
    culture: "en-US"
  }

  const response = await fetch(
    '/api/sessionm/points/qualifying',
    {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json'
      },
      body: JSON.stringify(payload)
    }
  )
  
  if (!response.ok) {
    throw new Error('Failed to run tier qualifying')
  }
  
  return response.json()
}

interface ZendeskErrorResponse {
  code?: string
}

type ZendeskAction = 'load' | 'create'

const zendeskErrorMessage = async (response: Response, action: ZendeskAction): Promise<string> => {
  const errorData: ZendeskErrorResponse = await response.json().catch(() => ({}))

  switch (errorData.code) {
    case 'credential_unavailable':
      return 'Zendesk access needs attention. Contact Digital Engineering before trying again.'
    case 'rate_limited':
      return 'Zendesk is handling too many requests. Wait a minute, then try again.'
    case 'upstream_timeout':
      return 'Zendesk did not respond in time. Try again. If this continues, contact Digital Engineering.'
    case 'upstream_unavailable':
      return 'Zendesk is temporarily unavailable. Try again. If this continues, contact Digital Engineering.'
    case 'upstream_rejected':
      return 'Zendesk rejected the ticket details. Check the fields and try again. If this continues, contact Digital Engineering.'
    case 'requester_ambiguous':
      return 'Multiple Zendesk requesters matched this email. Contact Digital Engineering before trying again.'
    case 'submission_outcome_unknown':
      return 'Zendesk did not confirm whether the ticket was created. Check Zendesk for an existing ticket before submitting again.'
    case 'invalid_request':
      return action === 'load'
        ? 'Enter a valid customer email, then try loading tickets again.'
        : 'Check the ticket details and try again.'
    default:
      if (response.status === 401) {
        return 'Your Admin Tool session expired. Refresh the page and sign in again.'
      }
      if (response.status === 403) {
        return 'You do not have access to this Zendesk action. Contact Digital Engineering if you need access.'
      }
      return action === 'load'
        ? 'Zendesk tickets could not be loaded. Try again. If this continues, contact Digital Engineering.'
        : 'The Zendesk ticket could not be submitted. Try again. If this continues, contact Digital Engineering.'
  }
}

const parseZendeskResponse = async <T>(response: Response, action: ZendeskAction): Promise<T> => {
  if (!response.ok) {
    throw new Error(await zendeskErrorMessage(response, action))
  }

  try {
    return await response.json()
  } catch {
    throw new Error('Zendesk returned an unreadable response. Try again. If this continues, contact Digital Engineering.')
  }
}

export const getZendeskTickets = async (email: string): Promise<any> => {
  // Auth is injected server-side by the Zendesk proxy (server/zendesk-api.js).
  const response = await fetch(`/api/zendesk/customer-tickets?email=${encodeURIComponent(email.trim())}`, {
    method: 'GET',
    headers: {
      'Content-Type': 'application/json'
    }
  })

  return parseZendeskResponse(response, 'load')
}

export const createZendeskTicket = async (ticketData: {
  subject: string
  comment: string
  requester_email: string
  requester_name: string
  tags?: string[]
  group_id?: number
  follower_email?: string
  custom_fields?: Array<{ id: number; value: string }>
}): Promise<any> => {
  const response = await fetch('/api/zendesk/tickets', {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json'
    },
    body: JSON.stringify(ticketData)
  })

  return parseZendeskResponse(response, 'create')
}

export const lookupMarianaTekUser = async (email: string): Promise<any> => {
  const response = await fetch(`/api/marianatek/api/users/?email=${encodeURIComponent(email)}`, {
    method: 'GET',
    headers: {
      'Content-Type': 'application/json'
    }
  })

  if (!response.ok) {
    throw new Error(`MarianaTek lookup failed: ${response.status}`)
  }

  return response.json()
}

export const lookupBarrysPlatformUser = async (email: string): Promise<any> => {
  const response = await fetch(`/api/barrys-platform/user/${encodeURIComponent(email)}`, {
    method: 'GET',
    headers: {
      'Content-Type': 'application/json'
    }
  })

  if (!response.ok) {
    throw new Error(`Barry's Platform lookup failed: ${response.status}`)
  }

  return response.json()
}

export const createUser = async (userData: any): Promise<any> => {
  const userPayload = {
    user: {
      opted_in: true,
      first_name: userData.firstName,
      last_name: userData.lastName,
      email: userData.email,
      date_of_birth: userData.dateOfBirth || null,
      phone_number: userData.phone || null,
      gender: userData.gender || null,
      address_line1: userData.address || null,
      city: userData.city || null,
      state_province: userData.state || null,
      postal_code: userData.zip || null,
      country: userData.country || null,
      user_profile: {
        loyalty_status: true
      },
      external_ids: [
        ...(userData.marianaTekId ? [{
          external_id_type: 'mariana_tek',
          external_id: userData.marianaTekId
        }] : []),
        ...(userData.barrysPlatformId ? [{
          external_id_type: 'barrys_platform',
          external_id: userData.barrysPlatformId
        }] : [])
      ]
    }
  }

  const response = await fetch(
    '/api/sessionm/users',
    {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json'
      },
      body: JSON.stringify(userPayload)
    }
  )
  
  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}))
    throw new Error(errorData.message || 'Failed to create user')
  }
  
  return response.json()
}

// Customer Merge API Functions
export const getAllPointBalances = async (userId: string): Promise<any> => {
  const payload = {
    retailer_id: API_CONFIG.retailerId,
    user_id: userId,
    culture: "en-US"
  }

  const response = await fetch(
    '/api/sessionm/points/all-balances',
    {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json'
      },
      body: JSON.stringify(payload)
    }
  )
  
  if (!response.ok) {
    throw new Error('Failed to get all point balances')
  }
  
  const data = await response.json()
  
  // Convert to the format expected by merge tool
  const details = data?.payload?.details || []
  const balances: {[key: string]: number} = {}
  
  details.forEach((d: any) => {
    const accountId = (d.point_account_id || "").toLowerCase()
    balances[accountId] = d.available_balance || 0
  })
  
  return balances
}

export const depositMergedPoints = async (userId: string, depositDetails: any[]): Promise<any> => {
  const payload = {
    retailer_id: API_CONFIG.retailerId,
    user_id: userId,
    deposit_details: depositDetails,
    allow_partial_success: false,
    disable_event_publishing: false,
    culture: "en-US"
  }

  const response = await fetch(
    '/api/sessionm/points/deposit',
    {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json'
      },
      body: JSON.stringify(payload)
    }
  )
  
  if (!response.ok) {
    throw new Error('Failed to deposit merged points')
  }
  
  return response.json()
}

export const issueOffersBulk = async (userId: string, offersToIssue: any[]): Promise<any> => {
  const payload = {
    user_id: userId,
    offers_to_issue: offersToIssue,
    retailer_id: API_CONFIG.retailerId,
    issue_exact_version: false,
    rollback_option: 0,
    culture: "en-US"
  }

  const response = await fetch(
    '/api/sessionm/offers/issue_multiple_bulk',
    {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json'
      },
      body: JSON.stringify(payload)
    }
  )
  
  if (!response.ok) {
    throw new Error('Failed to issue offers in bulk')
  }
  
  return response.json()
}

export const forgetUser = async (userId: string): Promise<any> => {
  const response = await fetch(
    `/api/sessionm/users/${encodeURIComponent(userId)}/privacy/forget`,
    {
      method: 'POST'
    }
  )
  
  if (!response.ok) {
    throw new Error('Failed to forget/delete user')
  }
  
  return response.json()
}

export const updateUserProfile = async (userId: string, payload: any): Promise<any> => {
  const response = await fetch(
    `/api/sessionm/users/${userId}`,
    {
      method: 'PUT',
      headers: {
        'Content-Type': 'application/json'
      },
      body: JSON.stringify(payload)
    }
  )
  
  if (!response.ok) {
    throw new Error('Failed to update user profile')
  }
  
  return response.json()
}

// Point account IDs from the Electron app (lowercased UUIDs)
export const POINT_ACCOUNTS = {
  SPEND_ON_BARRYS_X: "88d68bbe-819b-4ffd-ad76-0fdd3d7efdc4",
  SOCIAL: "8cca4d96-f9eb-45ea-a08b-d35bb7771fcf", 
  RETAIL_OR_FUEL: "79fe713a-7de8-4243-8b0a-8cc143eb6935",
  TAKE_A_CLASS: "03fe398b-cfb8-4be1-b8cb-f5325599a766",
  TIER_TRACKED: "1ab3fffd-539a-4f37-9002-89d19d95535d"
}

export const MERGE_POINT_SOURCE_ID = "9F34A0C7-4CFE-4193-9F96-FF2CE0FA00EB"

// Tier level IDs for tier promotion/demotion
export const TIER_LEVEL_IDS = {
  MEMBER: "ED977125-7237-4780-9637-6997C9632994",
  RED: "E67777B2-1575-453E-9F58-A431CBB92971", 
  SILVER: "154934B2-25A5-4592-AB60-8B7D982F710E",
  BLACK: "E3E479C5-9E19-4369-81A8-1882200CE35E"
}

export const moveTier = async (userId: string, tierLevelId: string, processIntermediateLevels: boolean = false): Promise<any> => {
  const payload = {
    retailer_id: API_CONFIG.retailerId,
    tier_system_id: "9980A7F9-2C44-427F-AF48-DF036D1602D1",
    tier_level_id: tierLevelId,
    user_id: userId,
    process_intermediate_levels: processIntermediateLevels,
    evaluate_qualifying_rules: false,
    process_entrance: true,
    culture: "en-US"
  }

  const response = await fetch(
    '/api/sessionm/tier/move_user',
    {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json'
      },
      body: JSON.stringify(payload)
    }
  )
  
  if (!response.ok) {
    throw new Error('Failed to move user tier')
  }
  
  return response.json()
}

export const lookupShopifyCustomer = async (email: string): Promise<any> => {
  const response = await fetch(`/api/shopify/customer?email=${encodeURIComponent(email)}`)

  if (!response.ok) {
    throw new Error(`Shopify lookup failed: ${response.status}`)
  }

  return response.json()
}

export const issueGiftCard = async (email: string, firstName: string, lastName: string, numClasses: number, currency?: string): Promise<any> => {
  const response = await fetch('/api/shopify/gift-card', {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json'
    },
    body: JSON.stringify({
      email,
      firstName,
      lastName,
      numClasses,
      currency
    })
  })

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}))
    throw new Error(errorData.error || `Failed to issue gift card: ${response.status}`)
  }

  return response.json()
}

export const issueCustomGiftCard = async (email: string, firstName: string, lastName: string, amount: number, note: string, currency: string, adminEmail?: string): Promise<any> => {
  const headers: Record<string, string> = { 'Content-Type': 'application/json' }
  const response = await fetch('/api/shopify/gift-card-custom', {
    method: 'POST',
    headers,
    body: JSON.stringify({
      email,
      firstName,
      lastName,
      amount,
      note,
      currency
    })
  })

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}))
    throw new Error(errorData.error || `Failed to issue custom gift card: ${response.status}`)
  }

  return response.json()
}

export const lookupGiftCardsByEmail = async (email: string): Promise<any> => {
  const response = await fetch(`/api/shopify/gift-cards?email=${encodeURIComponent(email)}`)

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}))
    throw new Error(errorData.error || `Failed to lookup gift cards: ${response.status}`)
  }

  return response.json()
}

export const deactivateGiftCard = async (giftCardId: string | number, currency?: string, adminEmail?: string): Promise<any> => {
  const headers: Record<string, string> = { 'Content-Type': 'application/json' }
  const response = await fetch(`/api/shopify/gift-cards/${giftCardId}/disable`, {
    method: 'POST',
    headers,
    body: JSON.stringify({ currency })
  })

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}))
    throw new Error(errorData.error || `Failed to deactivate gift card: ${response.status}`)
  }

  return response.json()
}

// AWS Athena API Functions
export const getTierHistory = async (marianaTekId: string): Promise<any> => {
  const response = await fetch(`/api/athena/tier-history/${encodeURIComponent(marianaTekId)}`)
  if (!response.ok) {
    throw new Error(`Failed to load tier history: ${response.status}`)
  }
  return response.json()
}

export const executeAthenaQuery = async (query: string, database: string, outputLocation: string): Promise<any> => {
  const response = await fetch('/api/athena/query', {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json'
    },
    body: JSON.stringify({
      query,
      database,
      outputLocation
    })
  })

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}))
    throw new Error(errorData.message || `Failed to execute Athena query: ${response.status}`)
  }

  return response.json()
}

export const getAthenaQueryStatus = async (queryExecutionId: string): Promise<any> => {
  const response = await fetch(`/api/athena/query/${queryExecutionId}/status`, {
    method: 'GET'
  })

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}))
    throw new Error(errorData.message || `Failed to get query status: ${response.status}`)
  }

  return response.json()
}

export const getAthenaQueryResults = async (queryExecutionId: string, nextToken?: string): Promise<any> => {
  const url = nextToken 
    ? `/api/athena/query/${queryExecutionId}/results?nextToken=${encodeURIComponent(nextToken)}`
    : `/api/athena/query/${queryExecutionId}/results`
    
  const response = await fetch(url, {
    method: 'GET'
  })

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}))
    throw new Error(errorData.message || `Failed to get query results: ${response.status}`)
  }

  return response.json()
}

export const cancelAthenaQuery = async (queryExecutionId: string): Promise<any> => {
  const response = await fetch(`/api/athena/query/${queryExecutionId}/cancel`, {
    method: 'POST'
  })

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}))
    throw new Error(errorData.message || `Failed to cancel query: ${response.status}`)
  }

  return response.json()
}

export const getAthenaDatabases = async (): Promise<any> => {
  const response = await fetch('/api/athena/databases', {
    method: 'GET'
  })

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}))
    throw new Error(errorData.message || `Failed to list databases: ${response.status}`)
  }

  return response.json()
}

export const getAthenaTables = async (database: string): Promise<any> => {
  const response = await fetch(`/api/athena/databases/${encodeURIComponent(database)}/tables`, {
    method: 'GET'
  })

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}))
    throw new Error(errorData.message || `Failed to list tables: ${response.status}`)
  }

  return response.json()
}

// Mariana Tek Reservations API Functions
export const getMarianaTekReservations = async (marianaTekId: string, monthsBack?: number, minDate?: string, maxDate?: string): Promise<any> => {
  let url = `/api/marianatek/api/reservations/?user=${encodeURIComponent(marianaTekId)}`

  // Use class_session_min_date and class_session_max_date for date filtering
  if (minDate && maxDate) {
    url += `&class_session_min_date=${minDate}&class_session_max_date=${maxDate}`
  } else if (monthsBack) {
    const date = new Date()
    date.setMonth(date.getMonth() - monthsBack)
    const fromDate = date.toISOString().split('T')[0] // Format: YYYY-MM-DD
    url += `&class_session_min_date=${fromDate}`
  }

  return fetchAllPaginatedData(url)
}

export const getMarianaTekClassSession = async (classSessionId: string): Promise<any> => {
  const response = await fetch(`/api/marianatek/api/class_sessions/${encodeURIComponent(classSessionId)}/`, {
    method: 'GET',
    headers: {
      'Content-Type': 'application/json'
    }
  })

  if (!response.ok) {
    throw new Error(`Failed to fetch class session: ${response.status}`)
  }

  return response.json()
}

export const getMarianaTekCreditTransaction = async (creditTransactionId: string): Promise<any> => {
  const response = await fetch(`/api/marianatek/api/credit_transactions/${encodeURIComponent(creditTransactionId)}/`, {
    method: 'GET',
    headers: {
      'Content-Type': 'application/json'
    }
  })

  if (!response.ok) {
    throw new Error(`Failed to fetch credit transaction: ${response.status}`)
  }

  return response.json()
}

export const getMarianaTekMembershipTransaction = async (membershipTransactionId: string): Promise<any> => {
  const response = await fetch(`/api/marianatek/api/membership_transactions/${encodeURIComponent(membershipTransactionId)}/`, {
    method: 'GET',
    headers: {
      'Content-Type': 'application/json'
    }
  })

  if (!response.ok) {
    throw new Error(`Failed to fetch membership transaction: ${response.status}`)
  }

  return response.json()
}

export const getMarianaTekMembershipInstance = async (membershipInstanceId: string): Promise<any> => {
  const response = await fetch(`/api/marianatek/api/membership_instances/${encodeURIComponent(membershipInstanceId)}/`, {
    method: 'GET',
    headers: {
      'Content-Type': 'application/json'
    }
  })

  if (!response.ok) {
    throw new Error(`Failed to fetch membership instance: ${response.status}`)
  }

  return response.json()
}

export const getMarianaTekMembershipTransactions = async (marianaTekUserId: string, fromDate?: string, toDate?: string): Promise<any> => {
  let url = `/api/marianatek/api/membership_transactions/?user=${encodeURIComponent(marianaTekUserId)}`

  if (fromDate) {
    url += `&from_date=${fromDate}`
  }
  if (toDate) {
    url += `&to_date=${toDate}`
  }

  return fetchAllPaginatedData(url)
}

// Helper function to handle paginated Mariana Tek API calls
const fetchAllPaginatedData = async (baseUrl: string): Promise<any> => {
  let allData: any[] = []
  let allIncluded: any[] = []
  let currentPage = 1
  let totalPages = 1

  while (currentPage <= totalPages) {
    const url = `${baseUrl}${baseUrl.includes('?') ? '&' : '?'}page=${currentPage}`

    const response = await fetch(url, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json'
      }
    })

    if (!response.ok) {
      throw new Error(`Failed to fetch data: ${response.status}`)
    }

    const pageData = await response.json()

    // Add this page's data
    if (pageData.data && Array.isArray(pageData.data)) {
      allData = allData.concat(pageData.data)
    }

    // Add this page's included data (for JSON:API relationships)
    if (pageData.included && Array.isArray(pageData.included)) {
      allIncluded = allIncluded.concat(pageData.included)
    }

    // Check pagination info
    if (pageData.meta?.pagination) {
      totalPages = pageData.meta.pagination.pages || 1
      currentPage++
    } else {
      // No pagination info, assume single page
      break
    }
  }

  return {
    data: allData,
    included: allIncluded,
    meta: {
      pagination: {
        count: allData.length,
        pages: 1,
        page: 1,
        per_page: allData.length
      }
    }
  }
}

export const getMarianaTekMemberships = async (marianaTekUserId: string): Promise<any> => {
  return fetchAllPaginatedData(`/api/marianatek/api/membership_instances/?user=${encodeURIComponent(marianaTekUserId)}`)
}

export const getMarianaTekCreditTransactions = async (marianaTekUserId: string): Promise<any> => {
  return fetchAllPaginatedData(`/api/marianatek/api/credit_transactions/?user=${encodeURIComponent(marianaTekUserId)}`)
}

export const getMarianaTekOrders = async (marianaTekUserId: string): Promise<any> => {
  return fetchAllPaginatedData(`/api/marianatek/api/orders/?user=${encodeURIComponent(marianaTekUserId)}`)
}

export const getMarianaTekGiftCardInstances = async (marianaTekUserId: string): Promise<any> => {
  const response = await fetch(`/api/marianatek/api/gift_card_instances/?purchased_by_user=${encodeURIComponent(marianaTekUserId)}`, {
    method: 'GET',
    headers: {
      'Content-Type': 'application/vnd.api+json',
      'Accept': 'application/vnd.api+json'
    }
  })

  if (!response.ok) {
    throw new Error(`Failed to fetch MT gift cards: ${response.status}`)
  }

  return response.json()
}

// Lookup gift cards received by a user via Athena
export const lookupReceivedGiftCards = async (email: string): Promise<any> => {
  const response = await fetch(`/api/athena/gift-cards?email=${encodeURIComponent(email)}`)

  if (!response.ok) {
    throw new Error(`Failed to lookup received gift cards: ${response.status}`)
  }

  return response.json()
}

// Transfer ID to SessionM
export const transferIdToSessionM = async (sessionmUserId: string, externalId: string, externalIdType: 'mariana_tek' | 'barrys_platform'): Promise<any> => {
  const response = await fetch(`/api/sessionm/users/${encodeURIComponent(sessionmUserId)}`, {
    method: 'PUT',
    headers: {
      'Content-Type': 'application/json'
    },
    body: JSON.stringify({
      user: {
        identifiers: [{
          external_id: externalId,
          external_id_type: externalIdType
        }]
      }
    })
  })

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}))
    throw new Error(errorData.message || `Failed to transfer ID to SessionM: ${response.status}`)
  }

  return response.json()
}

// SessionM Frequency Challenge API Functions (Red Room Challenge)
// Fetch all campaigns for a user
const getUserAllCampaigns = async (userId: string): Promise<any> => {
  const response = await fetch(`/api/sessionm/external/users/${userId}/campaigns`, {
    method: 'GET',
    headers: {
      'Content-Type': 'application/json'
    }
  })

  if (!response.ok) {
    return null
  }

  return response.json()
}

export const getUserFrequencyChallengeBranch = async (userId: string): Promise<any> => {
  const data = await getUserAllCampaigns(userId)

  if (!data?.campaigns?.promotions || !Array.isArray(data.campaigns.promotions)) {
    return null
  }

  // Find campaign 415 - Red Room Records Opt-in
  const branchCampaign = data.campaigns.promotions.find((c: any) =>
    c.campaign_id === 415 ||
    (c.name?.includes('Red Room') && c.name?.includes('Opt'))
  )

  if (!branchCampaign) {
    return null
  }

  const behaviors = branchCampaign?.progress?.behaviors

  if (!behaviors || !Array.isArray(behaviors)) {
    return null
  }

  // Filter out "Opt In Bonus Stars" behaviors
  const relevantBehaviors = behaviors.filter((behavior: any) =>
    behavior.progress === 1 &&
    !behavior.name?.includes('Opt In Bonus Stars')
  )

  console.log('Relevant behaviors found:', relevantBehaviors.length)
  console.log('Sample behavior data:', JSON.stringify(relevantBehaviors[0], null, 2))

  if (relevantBehaviors.length === 0) {
    return null
  }

  // Check for specific tier opt-ins (in priority order)

  // First priority: Check if user has "21+ Class Opt In"
  const behavior21Plus = relevantBehaviors.find((b: any) =>
    b.name?.includes('21+ Class Opt In')
  )
  if (behavior21Plus) {
    const optInDate = behavior21Plus.datetime || behavior21Plus.timestamp ||
                     behavior21Plus.completed_at || behavior21Plus.achieved_at ||
                     behavior21Plus.date || null
    console.log('21+ Opt-in date found:', optInDate, 'from behavior:', behavior21Plus)
    return {
      tier: '21+',
      tierNumber: 21,
      optInDate
    }
  }

  // Second priority: Check if user has BOTH "20 Class Opt In" AND "Upgrade - 21+"
  const behavior20 = relevantBehaviors.find((b: any) =>
    b.name?.includes('20 Class Opt In')
  )
  const behavior21Upgrade = relevantBehaviors.find((b: any) =>
    b.name?.includes('Upgrade - 21+')
  )
  if (behavior20 && behavior21Upgrade) {
    // Use the earlier date between 20 and 21+ upgrade
    const date20 = behavior20.datetime || behavior20.timestamp || behavior20.completed_at ||
                   behavior20.achieved_at || behavior20.date
    const date21 = behavior21Upgrade.datetime || behavior21Upgrade.timestamp ||
                   behavior21Upgrade.completed_at || behavior21Upgrade.achieved_at ||
                   behavior21Upgrade.date
    let optInDate = date20
    if (date21 && date20 && new Date(date21) < new Date(date20)) {
      optInDate = date21
    }
    console.log('20 Class + 21+ Upgrade dates:', { date20, date21, optInDate })
    return {
      tier: '21+',
      tierNumber: 21,
      optInDate: optInDate || null
    }
  }

  // Otherwise, find the first relevant behavior and extract tier from its name
  const activeBehavior = relevantBehaviors[0]
  const behaviorName = activeBehavior.name || ''

  let tier = null
  let tierNumber = null

  if (behaviorName.includes('20')) {
    tier = '20'
    tierNumber = 20
  } else if (behaviorName.includes('10')) {
    tier = '10'
    tierNumber = 10
  }

  if (!tier) {
    return null
  }

  const optInDate = activeBehavior.datetime || activeBehavior.timestamp ||
                   activeBehavior.completed_at || activeBehavior.achieved_at ||
                   activeBehavior.date || null
  console.log('Opt-in date for tier', tier, ':', optInDate, 'from behavior:', activeBehavior)

  return {
    tier,
    tierNumber,
    optInDate
  }
}

export const getUserFrequencyChallengeCount = async (userId: string): Promise<number> => {
  const data = await getUserAllCampaigns(userId)

  if (!data?.campaigns?.promotions || !Array.isArray(data.campaigns.promotions)) {
    return 0
  }

  // Find campaign 418 - Red Room Records 20 Class Check-ins
  const countCampaign = data.campaigns.promotions.find((c: any) =>
    c.campaign_id === 418 ||
    (c.name?.includes('Red Room') && c.name?.includes('20 Class'))
  )

  if (!countCampaign) {
    return 0
  }

  const behaviors = countCampaign?.behaviors

  if (!behaviors || typeof behaviors !== 'object') {
    return 0
  }

  // Look for "20 check_ins" behavior (note: underscore, not space)
  const checkinBehavior = behaviors['20 check_ins']

  if (!checkinBehavior) {
    return 0
  }

  // Use achieved count - this shows if they completed the 20 classes
  const achieved = checkinBehavior?.goals?.['reservation.checkedin']?.progress?.achieved

  // Return achieved count (1 means they hit 20, 0 means they're still working on it)
  const finalCount = typeof achieved === 'number' ? achieved * 20 : 0

  return finalCount
}

export const getUserFrequencyChallenge21PlusProgress = async (userId: string): Promise<number> => {
  const data = await getUserAllCampaigns(userId)

  if (!data?.campaigns?.promotions || !Array.isArray(data.campaigns.promotions)) {
    return 0
  }

  // Find campaign 419 - Red Room Records 21+ Class Challenge
  const plusCampaign = data.campaigns.promotions.find((c: any) =>
    c.campaign_id === 419 ||
    (c.name?.includes('Red Room') && c.name?.includes('21+'))
  )

  if (!plusCampaign) {
    return 0
  }

  const behaviors = plusCampaign?.behaviors

  if (!behaviors || typeof behaviors !== 'object') {
    return 0
  }

  // Get the "21st check_in" behavior - shows if they hit 21
  const check21 = behaviors['21st check_in']?.achieved || 0

  // Get the "22+ check_in" behavior - shows how many beyond 21
  const check22Plus = behaviors['22+ check_in']?.achieved || 0

  // Total is 21st class (1) + additional classes beyond 21
  return check21 + check22Plus
}

// Count checked-in classes from reservations (excluding free/comp classes)
export const countCheckedInClassesFromReservations = (reservationsData: any, optInDate: string | null): number => {
  if (!reservationsData?.data || !Array.isArray(reservationsData.data)) {
    return 0
  }

  const optInDateTime = optInDate ? new Date(optInDate).getTime() : 0

  return reservationsData.data.filter((reservation: any) => {
    // Must be checked in
    if (reservation.attributes?.status?.toLowerCase() !== 'check in') {
      return false
    }

    // Must be after opt-in date
    const checkInDate = reservation.attributes?.check_in_date
    if (checkInDate) {
      const checkInDateTime = new Date(checkInDate).getTime()
      if (checkInDateTime < optInDateTime) {
        return false
      }
    }

    // Check credit transaction for payment method
    // We need to look at included data or relationships
    // For now, we'll rely on credit_name being passed in the included data
    // This will be populated when reservations are fetched with details
    return true
  }).length
}

// NEW: Count from reservations with full details (credit names)
export const countChallengeClassesFromReservationsWithDetails = (reservationsWithDetails: any[], optInDate: string | null): number => {
  if (!reservationsWithDetails || !Array.isArray(reservationsWithDetails)) {
    return 0
  }

  const optInDateTime = optInDate ? new Date(optInDate).getTime() : 0

  return reservationsWithDetails.filter((reservation: any) => {
    // Must be checked in
    if (reservation.status?.toLowerCase() !== 'check in') {
      return false
    }

    // Must be after opt-in date
    const checkInDate = reservation.check_in_date
    if (checkInDate) {
      const checkInDateTime = new Date(checkInDate).getTime()
      if (checkInDateTime < optInDateTime) {
        return false
      }
    }

    return true
  }).length
}

export const getUserFrequencyChallengeMetrics = async (userId: string): Promise<any> => {
  try {
    // Get branch data (includes opt-in date now)
    const branchData = await getUserFrequencyChallengeBranch(userId)

    // Must have branch data (opted in to challenge)
    if (!branchData) {
      return null
    }

    console.log('Branch data received:', branchData)

    // Red Room Records: Always use October 1st, 2025 as opt-in date
    const redRoomOptInDate = '2025-10-01T00:00:00'

    // Return with opt-in date so caller can count from reservations
    return {
      currentCount: 0, // Will be calculated from reservations
      tier: branchData.tier,
      tierNumber: branchData.tierNumber,
      optInDate: redRoomOptInDate,
      label: `0 out of ${branchData.tier}` // Placeholder
    }
  } catch (error) {
    console.error(`Error fetching frequency challenge metrics for user ${userId}:`, error)
    return null
  }
}

// Extole API Functions
export const getExtolePersonByEmail = async (email: string): Promise<any> => {
  const response = await fetch(`/api/extole/persons?identity_key_value=${encodeURIComponent(email)}`, {
    method: 'GET',
    headers: {
      'Content-Type': 'application/json'
    }
  })

  const contentType = response.headers.get('content-type')

  if (!response.ok) {
    const errorText = await response.text()
    throw new Error(`Extole person lookup failed: ${response.status} - ${errorText}`)
  }

  if (!contentType || !contentType.includes('application/json')) {
    const responseText = await response.text()
    console.error('Non-JSON response from Extole API:', responseText.substring(0, 500))
    throw new Error(`Extole API returned non-JSON response: ${responseText.substring(0, 200)}`)
  }

  return response.json()
}

export const getExtolePersonById = async (personId: string): Promise<any> => {
  const response = await fetch(`/api/extole/persons/${personId}`, {
    method: 'GET',
    headers: {
      'Content-Type': 'application/json'
    }
  })

  const contentType = response.headers.get('content-type')

  if (!response.ok) {
    const errorText = await response.text()
    throw new Error(`Extole person lookup by ID failed: ${response.status} - ${errorText}`)
  }

  if (!contentType || !contentType.includes('application/json')) {
    const responseText = await response.text()
    console.error('Non-JSON response from Extole API:', responseText.substring(0, 500))
    throw new Error(`Extole API returned non-JSON response: ${responseText.substring(0, 200)}`)
  }

  return response.json()
}

export const getExtoleShareables = async (personId: string): Promise<any> => {
  const response = await fetch(`/api/extole/persons/${personId}/shareables`, {
    method: 'GET',
    headers: {
      'Content-Type': 'application/json'
    }
  })

  const contentType = response.headers.get('content-type')

  if (!response.ok) {
    const errorText = await response.text()
    throw new Error(`Extole shareables lookup failed: ${response.status} - ${errorText}`)
  }

  if (!contentType || !contentType.includes('application/json')) {
    const responseText = await response.text()
    console.error('Non-JSON response from Extole API:', responseText.substring(0, 500))
    throw new Error(`Extole API returned non-JSON response: ${responseText.substring(0, 200)}`)
  }

  return response.json()
}

export const getExtoleRewards = async (personId: string): Promise<any> => {
  const response = await fetch(`/api/extole/persons/${personId}/rewards`, {
    method: 'GET',
    headers: {
      'Content-Type': 'application/json'
    }
  })

  const contentType = response.headers.get('content-type')

  if (!response.ok) {
    const errorText = await response.text()
    throw new Error(`Extole rewards lookup failed: ${response.status} - ${errorText}`)
  }

  if (!contentType || !contentType.includes('application/json')) {
    const responseText = await response.text()
    console.error('Non-JSON response from Extole API:', responseText.substring(0, 500))
    throw new Error(`Extole API returned non-JSON response: ${responseText.substring(0, 200)}`)
  }

  return response.json()
}

// Mariana Tek Tag Management
export const addMarianaTekTag = async (marianaTekUserId: string, tagId: number): Promise<any> => {
  const payload = {
    data: {
      tag: tagId
    }
  }

  const response = await fetch(
    `/api/marianatek/api/users/${marianaTekUserId}/tag/`,
    {
      method: 'POST',
      headers: {
        'Content-Type': 'application/vnd.api+json',
        'Accept': 'application/vnd.api+json'
      },
      body: JSON.stringify(payload)
    }
  )

  const responseData = await response.json()

  if (!response.ok) {
    const errorMessage = responseData.error || responseData.message || responseData.details || `Failed to add tag (${response.status})`
    const err = new Error(errorMessage) as Error & { status?: number }
    err.status = response.status
    throw err
  }

  return responseData
}

export const removeMarianaTekTag = async (marianaTekUserId: string, tagId: number): Promise<any> => {
  const payload = {
    data: {
      tag: tagId
    }
  }

  const response = await fetch(
    `/api/marianatek/api/users/${marianaTekUserId}/untag/`,
    {
      method: 'POST',
      headers: {
        'Content-Type': 'application/vnd.api+json',
        'Accept': 'application/vnd.api+json'
      },
      body: JSON.stringify(payload)
    }
  )

  const responseData = await response.json()

  if (!response.ok) {
    const errorMessage = responseData.error || responseData.message || responseData.details || `Failed to remove tag (${response.status})`
    const err = new Error(errorMessage) as Error & { status?: number }
    err.status = response.status
    throw err
  }

  return responseData
}

// User Notes API Functions
export const getUserNotes = async (marianaTekUserId: string): Promise<any[]> => {
  const response = await fetch(
    `/api/marianatek/api/notes/?user=${encodeURIComponent(marianaTekUserId)}`,
    {
      method: 'GET',
      headers: {
        'Content-Type': 'application/vnd.api+json',
        'Accept': 'application/vnd.api+json'
      }
    }
  )

  if (!response.ok) {
    throw new Error(`Failed to fetch user notes: ${response.status}`)
  }

  const responseData = await response.json()

  // Extract notes from the response data
  const notes = responseData?.data || []

  // Fetch author details for each note
  const notesWithAuthors = await Promise.all(notes.map(async (note: any) => {
    const authorId = note.relationships?.author?.data?.id
    let authorName = 'Unknown'

    if (authorId) {
      try {
        // Fetch author details from Mariana Tek
        const authorResponse = await fetch(
          `/api/marianatek/api/users/${authorId}`,
          {
            method: 'GET',
            headers: {
              'Content-Type': 'application/vnd.api+json',
              'Accept': 'application/vnd.api+json'
            }
          }
        )

        if (authorResponse.ok) {
          const authorData = await authorResponse.json()
          const firstName = authorData?.data?.attributes?.first_name || ''
          const lastName = authorData?.data?.attributes?.last_name || ''
          authorName = `${firstName} ${lastName}`.trim() || 'Unknown'
        }
      } catch (err) {
        console.error('Failed to fetch author details:', err)
      }
    }

    return {
      id: note.id,
      text: note.attributes?.text || '',
      note_datetime: note.attributes?.note_datetime || null,
      created_at: note.attributes?.created_at || null,
      author_name: authorName
    }
  }))

  return notesWithAuthors
}

export const createUserNote = async (marianaTekUserId: string, noteText: string): Promise<any> => {
  const payload = {
    user_id: marianaTekUserId,
    text: noteText
  }

  const response = await fetch(
    '/api/marianatek/api/user_notes/',
    {
      method: 'POST',
      headers: {
        'Content-Type': 'application/vnd.api+json',
        'Accept': 'application/vnd.api+json'
      },
      body: JSON.stringify(payload)
    }
  )

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}))
    throw new Error(errorData.error || errorData.message || `Failed to create user note: ${response.status}`)
  }

  return response.json()
}

// Sync tier to Mariana Tek (triggers TIER_CHANGE webhook to backend)
export const syncTierToMarianaTek = async (email: string, currentTier: string): Promise<any> => {
  // Map tier names to the format expected by the backend webhook
  const tierNameMap: { [key: string]: string } = {
    'Member': 'Member',
    'Red': 'Red',
    'Silver': 'Silver',
    'Black': 'Black'
  }

  const tierName = tierNameMap[currentTier] || currentTier

  const payload = {
    operationId: crypto.randomUUID(),
    email,
    destinationTier: tierName
  }

  const response = await fetch('/api/barrys-backend/webhooks/session-m', {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json'
    },
    body: JSON.stringify(payload)
  })

  if (!response.ok) {
    await throwServerError(response, 'Failed to sync tier to Mariana Tek')
  }

  return response.json()
}

// ============================================
// Find Your Strength 2026 Challenge API Functions
// ============================================

/**
 * Get a specific user's Find Your Strength 2026 challenge progress
 * @param userId - The user's ID (UUID or MT ID)
 * @returns User's challenge progress or null if not opted in
 */
export const getFYSUserProgress = async (userId: string): Promise<any> => {
  const response = await fetch(`/api/fys/user/${encodeURIComponent(userId)}`, {
    method: 'GET',
    headers: {
      'Content-Type': 'application/json'
    }
  })

  if (!response.ok) {
    throw new Error(`FYS user lookup failed: ${response.status}`)
  }

  return response.json()
}

/**
 * Check which users from a list are FYS challenge participants
 * @param userIds - Array of user IDs (MT IDs) to check
 * @returns Object with participants array and count
 */
export const checkFYSParticipants = async (userIds: string[]): Promise<any> => {
  if (!userIds || userIds.length === 0) {
    return { participants: [], count: 0 }
  }

  const response = await fetch('/api/fys/check-participants', {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json'
    },
    body: JSON.stringify({ user_ids: userIds })
  })

  if (!response.ok) {
    throw new Error(`FYS check-participants failed: ${response.status}`)
  }

  return response.json()
}

/**
 * Get detailed challenge progress for multiple users
 * @param userIds - Array of user IDs (MT IDs) to check
 * @returns Object with participants array containing detailed progress
 */
export const getFYSParticipantsDetails = async (userIds: string[]): Promise<any> => {
  if (!userIds || userIds.length === 0) {
    return { participants: [] }
  }

  const response = await fetch('/api/fys/participants-details', {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json'
    },
    body: JSON.stringify({ user_ids: userIds })
  })

  if (!response.ok) {
    throw new Error(`FYS participants-details failed: ${response.status}`)
  }

  return response.json()
}

/**
 * Format FYS challenge progress for display
 * @param progressData - Raw progress data from API
 * @returns Formatted progress data for UI or null if not opted in
 */
export const formatFYSProgress = (progressData: any): any => {
  if (!progressData?.opted_in || !progressData?.user_progress) {
    return null
  }

  const progress = progressData.user_progress
  const challenge = progressData.challenge

  return {
    challengeName: challenge?.name || 'Find Your Strength 2026',
    challengeStartDate: challenge?.start_at ? new Date(challenge.start_at) : null,
    challengeEndDate: challenge?.end_at ? new Date(challenge.end_at) : null,
    sprintName: progress.sprint_name,
    sprintGoal: progress.sprint_goal,
    classesCompleted: progress.classes_completed,
    progressPercentage: progress.progress_percentage,
    optInDate: progress.opt_in_date ? new Date(progress.opt_in_date) : null,
    isCompleted: progress.completed,
    completedAt: progress.completed_at ? new Date(progress.completed_at) : null,
    classesRemaining: Math.max(0, (progress.sprint_goal || 0) - (progress.classes_completed || 0))
  }
}

export const lookupFYSPrize = async (email: string): Promise<any> => {
  const response = await fetch(`/api/athena/fys-prize?email=${encodeURIComponent(email)}`)

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}))
    throw new Error(errorData.error || `Failed to lookup FYS prize: ${response.status}`)
  }

  return response.json()
}

export const updateFYSPrize = async (prizeData: {
  email: string
  first_name?: string
  last_name?: string
  prize_choice: string
  hoodie_choice?: string
  hoodie_color?: string
  sizing?: string
  water_bottle_choice?: string
  challenge_track?: string
  address?: string
  address_line2?: string
  city?: string
  state?: string
  zip_code?: string
  country?: string
}): Promise<any> => {
  const response = await fetch('/api/athena/fys-prize', {
    method: 'PUT',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(prizeData)
  })

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}))
    throw new Error(errorData.error || `Failed to update FYS prize: ${response.status}`)
  }

  return response.json()
}

export const triggerChallengeEvent = async (userId: string, events: object): Promise<any> => {
  const response = await fetch(
    `/api/sessionm/users/${userId}/events`,
    {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json'
      },
      body: JSON.stringify({ events })
    }
  )

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}))
    throw new Error(errorData.error || `Failed to trigger challenge event: ${response.status}`)
  }

  return response.json()
}

export const lookupDiscountCodes = async (email: string): Promise<any> => {
  const response = await fetch(`/api/discount-codes?email=${encodeURIComponent(email)}`)

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}))
    throw new Error(errorData.error || `Failed to lookup discount codes: ${response.status}`)
  }

  return response.json()
}

export const resendGiftCardNotification = async (giftCardId: string | number, email: string, currency?: string): Promise<any> => {
  const response = await fetch('/api/shopify/gift-card-resend-notification', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ giftCardId, email, currency })
  })

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}))
    throw new Error(errorData.error || `Failed to resend notification: ${response.status}`)
  }

  return response.json()
}

// ============================================
// UWB 2026 - Bag-order cancel (admin-site frontend wrappers)
// ============================================

export type UwbBagOrder = {
  id: string
  name: string
  shop: 'us' | 'uk' | 'ie'
  currency: string
  total: string
  created_at: string
  fulfillment_status: string | null
  financial_status: string | null
  cancelled_at: string | null
  closed_at: string | null
  refunds_count: number
  tags: string[]
  line_items: { sku: string; quantity: number }[]
  is_uwb_bag: boolean
  can_cancel: boolean
  block_reason: string | null
}

export const listUwbBagOrders = async (
  email: string,
  adminEmail?: string,
): Promise<{ ok: boolean; orders: UwbBagOrder[] }> => {
  const headers: Record<string, string> = {}
  const response = await fetch(`/api/shopify/uwb-orders?email=${encodeURIComponent(email)}`, { headers })
  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}))
    throw new Error(errorData.error || errorData.message || `Failed to list UWB bag orders: ${response.status}`)
  }
  const body = await response.json()
  return { ok: !!body?.ok, orders: Array.isArray(body?.orders) ? body.orders : [] }
}

export const cancelUwbBagOrder = async (params: {
  order_id: string | number
  shop: 'us' | 'uk' | 'ie'
  admin_notes?: string
  adminEmail?: string
}): Promise<{
  ok: boolean
  order_id?: string
  shop?: 'us' | 'uk' | 'ie'
  cancelled_at?: string | null
  error_code?: string
  message?: string
}> => {
  const headers: Record<string, string> = { 'Content-Type': 'application/json' }
  const response = await fetch('/api/shopify/order-cancel', {
    method: 'POST',
    headers,
    body: JSON.stringify({
      order_id: params.order_id,
      shop: params.shop,
      admin_notes: params.admin_notes,
    }),
  })
  let parsed: any = null
  try { parsed = await response.json() } catch { parsed = null }
  if (!response.ok) {
    return {
      ok: false,
      error_code: parsed?.error_code || 'request_failed',
      message: parsed?.message || parsed?.error || `Failed to cancel order: ${response.status}`,
    }
  }
  return {
    ok: !!parsed?.ok,
    order_id: parsed?.order_id,
    shop: parsed?.shop,
    cancelled_at: parsed?.cancelled_at ?? null,
    error_code: parsed?.error_code,
    message: parsed?.message,
  }
}

export const cdpCancelBagOrder = async (params: {
  registrationId: string
  shopify_order_id: number
  shopify_shop: 'us' | 'uk' | 'ie'
  canceled_notes?: string
  adminEmail?: string
}): Promise<{
  ok: boolean
  registration_id?: string
  prize_order_canceled_at?: string | null
  prize_order_canceled_by?: string | null
  prize_order_canceled_notes?: string | null
  error_code?: string
  message?: string
}> => {
  const headers: Record<string, string> = { 'Content-Type': 'application/json' }
  const response = await fetch(
    `/api/uwb/admin/registration/${encodeURIComponent(params.registrationId)}/cancel-bag-order`,
    {
      method: 'POST',
      headers,
      body: JSON.stringify({
        shopify_order_id: params.shopify_order_id,
        shopify_shop: params.shopify_shop,
        canceled_notes: params.canceled_notes,
      }),
    }
  )
  let parsed: any = null
  try { parsed = await response.json() } catch { parsed = null }
  if (!response.ok) {
    return {
      ok: false,
      error_code: parsed?.error_code || 'request_failed',
      message: parsed?.error || parsed?.message || `Failed CDP cancel-bag-order: ${response.status}`,
    }
  }
  return {
    ok: true,
    registration_id: parsed?.registration_id,
    prize_order_canceled_at: parsed?.prize_order_canceled_at ?? null,
    prize_order_canceled_by: parsed?.prize_order_canceled_by ?? null,
    prize_order_canceled_notes: parsed?.prize_order_canceled_notes ?? null,
  }
}

export const deactivateDiscountCode = async (discountCode: string, email: string): Promise<any> => {
  const response = await fetch('/api/discount-codes/deactivate', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({
      discountCode,
      email
    })
  })

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}))
    throw new Error(errorData.error || `Failed to deactivate discount code: ${response.status}`)
  }

  return response.json()
}

export { POINT_SOURCES, AVAILABLE_OFFERS, AVAILABLE_OFFER_ROOT_IDS }
